import { Typography, Chip, Stack, Link } from '@mui/material';
import { DetailSection, DetailField } from './DetailSection';
import { severityChipSx, stateChipSx } from './severity';
import { CK, statusChipSx } from '../portalStyles';

const fmt = (t) => {
  if (!t) return '';
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

// AWS's FindingType enum, prettified. The keys are lower-cased because the value
// arrives from a database column and the backend's own switch on it
// (accessAnalyzerRemediation) matches case-insensitively for exactly that reason;
// an unknown type falls through to its raw enum rather than vanishing.
//
// Module-local, like inspectorTypeLabel: exporting it would mean the shared
// findings list - which serves five other engines - imports an Access Analyzer
// mapper to prettify one chip, and raw-enum rendering in that table is the
// existing behaviour for every engine.
const TYPE_LABELS = {
  externalaccess: 'External access',
  unusediamrole: 'Unused IAM role',
  unusediamuseraccesskey: 'Unused access key',
  unusediamuserpassword: 'Unused console password',
  unusedpermission: 'Unused permission',
};
const typeLabel = (v) => TYPE_LABELS[String(v || '').toLowerCase()] || v || '';

// The two families need different bodies, and `findingType` is the discriminator
// the backend also splits on. Anything starting "unused" belongs to the paid
// ACCOUNT_UNUSED_ACCESS analyzer; everything else is external access.
const isUnusedFamily = (v) => String(v || '').toLowerCase().startsWith('unused');

// How many principals/actions/conditions are rendered before the rest collapse
// into a "+N more" chip. A wildcard resource policy legitimately grants a hundred
// actions, and a hundred chips turns the drawer into a scroll well that buries the
// remediation block underneath it.
const MAX_CHIPS = 20;

// ChipList renders a []string as wrapped neutral chips, or nothing at all when the
// list is empty - an empty section header with no rows under it reads as missing
// data rather than as "AWS reported none".
function ChipList({ values }) {
  const list = Array.isArray(values) ? values.filter(Boolean) : [];
  if (list.length === 0) return null;
  const shown = list.slice(0, MAX_CHIPS);
  const extra = list.length - shown.length;
  return (
    <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 0.5 }}>
      {shown.map((v) => <Chip key={v} size="small" label={v} sx={stateChipSx} />)}
      {extra > 0 && <Chip size="small" label={`+${extra} more`} sx={stateChipSx} />}
    </Stack>
  );
}

// AWS IAM Access Analyzer finding body.
//
// Two finding families share this panel because they share one AWS service, one
// cache table and one detail route - but they answer different questions, so the
// sections below are gated on the family rather than rendered as a union with half
// the rows dashed out:
//
//   external access  who OUTSIDE the account can reach this resource, doing what
//   unused access    which granted access has not been used in the tracking window
//
// No status chip here: the drawer's own sticky header already carries one for
// every engine, and a second copy scrolling underneath it is the kind of duplicate
// that starts disagreeing.
export default function AccessAnalyzerDetailBody({ finding }) {
  if (!finding) return null;

  const f = finding;
  const remediation = f.remediation || {};
  const type = f.findingType || '';
  const unused = isUnusedFamily(type);

  // isPublic is a *bool on the wire and the pointer is load-bearing. null means
  // AWS did not report the attribute at all (every error finding is in that
  // state), NOT "not public": rendering null as "Cross-account" would tell an
  // operator that a resource Prism could not evaluate is merely shared internally.
  const exposure = f.isPublic === true
    ? { label: 'Public - reachable by anyone', intent: 'error' }
    : f.isPublic === false
      ? { label: 'Cross-account', intent: 'warning' }
      : { label: 'Not reported by AWS', intent: 'neutral' };

  // LastAccessed is nullable ON PURPOSE and null is the WORST case, not the best:
  // it means the role, key or password was never used at all. "Never used" is
  // therefore printed in words - a blank row, or the year-1 timestamp a non-pointer
  // column would have produced, reads as missing data.
  const lastAccessed = f.lastAccessed ? fmt(f.lastAccessed) : 'Never used';

  return (
    <>
      <DetailSection title="Overview">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
          {type && <Chip size="small" label={typeLabel(type)} sx={stateChipSx} />}
          {f.severity && <Chip size="small" label={f.severity} sx={severityChipSx(f.severity)} />}
        </Stack>
        {f.description && (
          <Typography variant="body2" sx={{ mb: 1.5, color: CK.navy }}>
            {f.description}
          </Typography>
        )}
        {/* Severity is stated as Prism's, here and on the dashboard, because a
            customer WILL diff this panel against the AWS console: IAM Access
            Analyzer ships no severity field at all, and its Security Hub export is
            a flat LOW for everything from a world-readable bucket to an unused
            permission. */}
        <Typography variant="body2" sx={{ mb: 1.5, color: CK.muted }}>
          Severity is calculated by Prism from the finding&apos;s own evidence - IAM
          Access Analyzer does not publish one.
        </Typography>
        {/* findingId is the AWS finding id (a service-generated UUID), which is what
            an operator pastes into the Access Analyzer console. `id` is Prism's
            synthetic row uuid and is deliberately not labelled as AWS's. */}
        <DetailField label="Finding ID" value={f.findingId} />
        <DetailField label="Finding type" value={typeLabel(type)} />
        <DetailField label="Account" value={f.accountId} />
        {/* The account that OWNS the resource is not always the account whose
            analyzer produced the finding, so it is a row of its own rather than an
            assumed duplicate of the one above. */}
        <DetailField label="Resource owner" value={f.resourceOwnerAccount} />
        <DetailField label="Analyzer region" value={f.region} />
        <DetailField label="First seen" value={fmt(f.firstObservedAt)} />
        <DetailField label="Last seen" value={fmt(f.lastObservedAt)} />
        <DetailField label="Analyzed by AWS" value={fmt(f.analyzedAtAws)} />
      </DetailSection>

      {!unused && (
        <DetailSection title="External access">
          <DetailField
            label="Exposure"
            value={<Chip size="small" label={exposure.label} sx={statusChipSx(exposure.intent)} />}
          />
          {/* The zone-of-trust caveat is repeated on every external-access finding,
              not just in the setup wizard, because this is the panel an operator
              opens when they think Prism has raised a false positive. Prism creates
              ACCOUNT-scoped analyzers, so a grant to another account in the
              customer's OWN organization is external here while an
              organization-scoped analyzer would call it trusted. */}
          <Typography variant="body2" sx={{ mt: 1, mb: 1.5, color: CK.muted }}>
            Prism&apos;s analyzers are account-scoped, so another account in your own
            AWS Organization counts as external. That access is real - it is simply
            trusted by an organization-scoped analyzer and reported by this one.
          </Typography>
          <DetailField label="Principal" value={<ChipList values={f.principal} />} />
          <DetailField label="Actions" value={<ChipList values={f.actions} />} />
          <DetailField label="Conditions" value={<ChipList values={f.condition} />} />
        </DetailSection>
      )}

      {unused && (
        <DetailSection title="Unused access">
          <DetailField label="Last used" value={lastAccessed} />
          {/* Said in words because the row above cannot say it: "Never used" is the
              worst case in this section, not the cleanest. */}
          {!f.lastAccessed && (
            <Typography variant="body2" sx={{ mt: 1, color: CK.muted }}>
              AWS has no record of this credential or permission ever being used -
              the strongest case for removing it, not the weakest.
            </Typography>
          )}
        </DetailSection>
      )}

      {/* An error finding is a COVERAGE GAP, not a clean result: the analyzer could
          not evaluate the resource at all, so it carries no isPublic, no principal
          and no action. Silently dropping these is exactly what a posture product
          must not do, so the panel names the error code. */}
      {f.findingError && (
        <DetailSection title="Analysis error">
          <DetailField
            label="Error"
            value={<Chip size="small" label={f.findingError} sx={statusChipSx('warning')} />}
          />
          <Typography variant="body2" sx={{ mt: 1, color: CK.muted }}>
            IAM Access Analyzer could not evaluate this resource, so its exposure is
            unknown rather than clean.
          </Typography>
        </DetailSection>
      )}

      <DetailSection title="Resource">
        <DetailField label="Resource type" value={f.resourceType} />
        <DetailField label="Resource" value={f.resource} />
      </DetailSection>

      {(remediation.text || remediation.url) && (
        <DetailSection title="Remediation">
          {remediation.text && (
            <Typography variant="body2" sx={{ mb: 0.5, color: CK.navy, wordBreak: 'break-word' }}>
              {remediation.text}
            </Typography>
          )}
          {remediation.url && (
            <Link href={remediation.url} target="_blank" rel="noreferrer">View documentation</Link>
          )}
        </DetailSection>
      )}
    </>
  );
}
